import {defineStore} from 'pinia'
import {useApiRequestStore} from '../../ApiRequestStore/useApiRequestStore'
import {
  ILoginRequestInterfaces,
} from '@toolify/server/src/controllers/AuthController/validators/Login/types/ILoginRequestInterfaces'
import {
  IRefreshTokenRequestInterfaces,
} from '@toolify/server/src/controllers/AuthController/validators/RefreshToken/types/IRefreshTokenRequestInterfaces'
import {IRegisterRequestInterfaces} from '@toolify/server/src/controllers/AuthController/validators/Register/types/IRegisterRequestInterfaces'
import {
  IChangePasswordRequestInterfaces,
} from '@toolify/server/src/controllers/AuthController/validators/ChangePassword/types/IChangePasswordRequestInterfaces'
import {
  IForgotPasswordRequestInterfaces,
} from '@toolify/server/src/controllers/AuthController/validators/ForgotPassword/types/IForgotPasswordRequestInterfaces'
import {OperatingSystemType} from '@toolify/server/src/controllers/NativeAppController/enum/OperatingSystemType'
import {IOperatingSystemTypeInterfacesMap} from '@toolify/server/src/services/OperatingSystemService/types/IOperatingSystemTypeInterfacesMap'
import {
  IGetIdentityProvidersRequestInterfaces,
} from '@toolify/server/src/controllers/AuthController/validators/GetIdentityProviders/types/IGetIdentityProvidersRequestInterfaces'
import {
  IOAuthAuthorizeRequestInterfaces,
} from '@toolify/server/src/controllers/AuthController/validators/OAuthAuthorize/types/IOAuthAuthorizeRequestInterfaces'
import {
  IOAuthCallbackRequestInterfaces,
} from '@toolify/server/src/controllers/AuthController/validators/OAuthCallback/types/IOAuthCallbackRequestInterfaces'
import {IdentityProvider} from '@toolify/server/src/services/OAuthService/enum/IdentityProvider'
import {AppPlatformType} from '@toolify/server/src/models/mongoose/SessionModel/enum/AppPlatformType'
import {
  ICreateSessionRequestInterfaces,
} from '@toolify/server/src/controllers/AuthController/validators/CreateSession/types/ICreateSessionRequestInterfaces'

export const useAuthApiStore = defineStore('authApi', {
  actions: {
    async getIdentityProviders(): Promise<IGetIdentityProvidersRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.get<IGetIdentityProvidersRequestInterfaces>('/auth/identity-providers')
    },

    async createSession(
      appPlatformType: AppPlatformType,
      operatingSystemType: OperatingSystemType,
      operatingSystemData?: IOperatingSystemTypeInterfacesMap[OperatingSystemType]['payload'],
    ): Promise<ICreateSessionRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {token, expiresAt} = await apiRequestStore.post<ICreateSessionRequestInterfaces>('/auth/session', {
        data: {
          appPlatformType,
          operatingSystemType,
          operatingSystemData,
        },
      }, {
        bypassQueueLimits: true,
        hideErrorReferenceToast: true,
      })
      return {
        token,
        expiresAt: new Date(expiresAt),
      }
    },

    async getIdentityProviderAuthorizationUrl(
      provider: IdentityProvider,
      secret?: string,
    ): Promise<IOAuthAuthorizeRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.get<IOAuthAuthorizeRequestInterfaces>(`/auth/oauth/${provider}/authorize`, {
        params: {
          secret,
        },
      })
    },

    async oauthCallback(
      provider: IdentityProvider,
      code: string,
      operatingSystemType: OperatingSystemType,
      appPlatformType: AppPlatformType,
      operatingSystemData?: IOperatingSystemTypeInterfacesMap[OperatingSystemType]['payload'],
      secret?: string,
    ): Promise<IOAuthCallbackRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const requestData: IOAuthCallbackRequestInterfaces['body'] = {
        provider,
        code,
        operatingSystemType,
        appPlatformType,
        secret,
      }
      if (operatingSystemData) {
        requestData.operatingSystemData = operatingSystemData
      }
      const {token, expiresAt, userId} = await apiRequestStore.post<IOAuthCallbackRequestInterfaces>('/auth/oauth/callback', {
        data: requestData,
      })
      return {
        token,
        expiresAt: new Date(expiresAt),
        userId,
      }
    },

    async loginWithCredentials(
      login: string,
      password: string,
      operatingSystemType: string,
      appPlatformType?: string,
      operatingSystemData?: IOperatingSystemTypeInterfacesMap[OperatingSystemType]['payload'],
      captcha?: string,
    ): Promise<ILoginRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {
        token,
        expiresAt,
        userId,
      } = await apiRequestStore.post<ILoginRequestInterfaces>('/auth/login', {
        data: {
          login,
          password,
          captcha,
          operatingSystemType,
          operatingSystemData,
          appPlatformType,
        },
      })
      return {
        expiresAt: new Date(expiresAt),
        token: token,
        userId,
      }
    },

    async register(
      name: string,
      password: string,
      mail: string,
      appPlatformType: string,
      operatingSystemType: string,
      operatingSystemData?: IOperatingSystemTypeInterfacesMap[OperatingSystemType]['payload'],
      continuePath?: string,
      localeType?: string,
      themeType?: string,
    ): Promise<IRegisterRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {mail: receivedMail, verificationId, isVerified} = await apiRequestStore.post<IRegisterRequestInterfaces>('/auth/register', {
        data: {
          name,
          password,
          mail,
          continuePath,
          appPlatformType,
          operatingSystemType,
          operatingSystemData,
          localeType,
          themeType,
        },
      })
      return {
        mail: receivedMail,
        verificationId,
        isVerified,
      }
    },

    async forgotPassword(
      mail: string,
      appPlatformType: string,
      operatingSystemType: string,
      operatingSystemData?: IOperatingSystemTypeInterfacesMap[OperatingSystemType]['payload'],
      continuePath?: string,
    ): Promise<IForgotPasswordRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {mail: receivedMail, verificationId} = await apiRequestStore.post<IForgotPasswordRequestInterfaces>('/auth/forgot-password', {
        data: {
          mail,
          continuePath,
          appPlatformType,
          operatingSystemType,
          operatingSystemData,
        },
      })
      return {
        mail: receivedMail,
        verificationId,
      }
    },

    async changePassword(
      verificationId: string,
      verificationSecret: string,
      password: string,
    ): Promise<IChangePasswordRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {token, expiresAt} = await apiRequestStore.put<IChangePasswordRequestInterfaces>('/auth/change-password', {
        data: {
          verificationId,
          verificationSecret,
          password,
        },
      })
      return {
        token,
        expiresAt: new Date(expiresAt),
      }
    },

    async refreshToken(): Promise<IRefreshTokenRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {
        token,
        expiresAt,
      } = await apiRequestStore.get<IRefreshTokenRequestInterfaces>('/auth/refresh-token', {
      }, {
        priority: 2,
        bypassQueueLimits: true,
        hideErrorReferenceToast: true,
      })
      return {
        expiresAt: new Date(expiresAt),
        token: token,
      }
    },
  },
})
